import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Award, Plus, Search, Check, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api, { graduierungAPI } from '@/lib/api';

/* Graduierungs-Übersicht (22.09.2026, Regelwerk): alle aktiven Mitglieder mit
   Gürtel, Streifen, Trainings und den drei Kriterien für die nächste Stufe
   (Trainings · Haltezeit · Streifen). Reife zuerst. Streifen +1 und direkte
   Beförderung für Trainer. */

function tageDE(iso) {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return '–'; }
}

export default function GraduierungUebersicht() {
  const navigate = useNavigate();
  const [eintraege, setEintraege] = useState([]);
  const [laden, setLaden] = useState(true);
  const [suche, setSuche] = useState('');
  const [nurReif, setNurReif] = useState(false);
  const [busy, setBusy] = useState(null);
  // Einstufen von Hand (28.09.2026): Gürtel ohne Push + Einheiten bis zur Zielzahl nachtragen
  const [stufenListe, setStufenListe] = useState([]);
  const [einstufung, setEinstufung] = useState(null);   // {e, stufe, einheiten}
  useEffect(() => { graduierungAPI.stufen().then(r => setStufenListe(r.data.stufen || [])).catch(() => {}); }, []);
  const einstufenSpeichern = async () => {
    const { e, stufe, einheiten } = einstufung;
    setBusy(e.member_id);
    try {
      const r = await graduierungAPI.einstufen(e.member_id, stufe, einheiten === '' ? null : Number(einheiten));
      toast.success(`${e.name}: ${r.data.label}, ${r.data.einheiten_jetzt} Einheiten${r.data.nachgetragen ? ` (${r.data.nachgetragen} nachgetragen)` : ''}`);
      setEinstufung(null); lade();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Einstufen fehlgeschlagen'); }
    setBusy(null);
  };

  const lade = useCallback(async () => {
    setLaden(true);
    try {
      const r = await graduierungAPI.uebersicht();
      setEintraege(r.data.eintraege || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übersicht konnte nicht geladen werden'); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { lade(); }, [lade]);

  const streifen = async (e) => {
    setBusy(e.member_id);
    try {
      const r = await graduierungAPI.streifen(e.member_id, 1);
      toast.success(`${e.name}: ${r.data.streifen}. Streifen vergeben`);
      lade();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Streifen konnte nicht vergeben werden'); }
    finally { setBusy(null); }
  };

  const graduieren = async (e) => {
    if (!e.naechste) return;
    if (!window.confirm(`${e.name} jetzt zum ${e.naechste.label} graduieren?\n\nGürtel wird gesetzt, Streifen auf 0, das Mitglied bekommt eine Benachrichtigung.`)) return;
    setBusy(e.member_id);
    try {
      const r = await graduierungAPI.graduieren(e.member_id, e.naechste.key);
      toast.success(`${e.name} zum ${e.naechste.label} graduiert`);
      lade();
      if (r.data?.id) urkundeOeffnen(e.member_id, r.data.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Graduierung fehlgeschlagen'); }
    finally { setBusy(null); }
  };

  const urkundeOeffnen = async (memberId, gradId) => {
    try {
      const r = await api.get(graduierungAPI.urkundeUrl(memberId, gradId), { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Urkunde konnte nicht erzeugt werden'); }
  };

  const q = suche.trim().toLowerCase();
  const liste = eintraege.filter((e) => (!nurReif || e.reif) && (!q || (e.name || '').toLowerCase().includes(q)));
  const reifAnzahl = eintraege.filter((e) => e.reif).length;

  return (
    <div className="space-y-3" data-testid="graduierung-uebersicht">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={suche} onChange={(ev) => setSuche(ev.target.value)} placeholder="Mitglied suchen…" className="pl-9" data-testid="grad-suche" />
        </div>
        <Button size="sm" variant={nurReif ? 'default' : 'outline'} onClick={() => setNurReif((v) => !v)} data-testid="grad-nur-reif">
          <Award className="w-3.5 h-3.5 mr-1" /> Nur reife ({reifAnzahl})
        </Button>
        <span className="text-xs text-muted-foreground sm:ml-auto">{liste.length} von {eintraege.length} Mitgliedern</span>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  {['Mitglied', 'Gürtel', 'seit', 'Streifen', 'Trainings', 'Nächste Stufe', 'Fehlt', ''].map((h) => (
                    <th key={h} className="text-left p-3 text-xs font-semibold text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {laden && (
                  <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Lädt…</td></tr>
                )}
                {!laden && liste.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Keine Mitglieder</td></tr>
                )}
                {!laden && liste.map((e) => {
                  const kTr = e.kriterien.find((k) => k.key === 'trainings');
                  return (
                    <tr key={e.member_id} className={`border-b border-border/30 ${e.reif ? 'bg-emerald-500/5' : ''}`} data-testid={`grad-row-${e.member_id}`}>
                      <td className="p-3 font-medium">
                        <button type="button" className="hover:underline text-left" onClick={() => navigate(`/leads/${e.member_id}`)}>{e.name}</button>
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-full border border-border" style={{ background: e.belt_color || '#eee' }} />
                          {e.belt_label}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">{tageDE(e.belt_seit)}</td>
                      <td className="p-3">
                        <span className="inline-flex gap-0.5" title={`${e.streifen} von ${e.streifen_je_gurt}`}>
                          {[...Array(e.streifen_je_gurt)].map((_, i) => (
                            <span key={i} className={`w-2 h-4 rounded-sm ${i < e.streifen ? 'bg-foreground' : 'bg-muted'}`} />
                          ))}
                        </span>
                      </td>
                      <td className="p-3 text-xs">{e.trainings}{kTr ? <span className="text-muted-foreground"> / {kTr.soll}</span> : null}</td>
                      <td className="p-3 text-xs">{e.naechste ? e.naechste.label : <span className="text-muted-foreground">höchste Stufe</span>}</td>
                      <td className="p-3">
                        {e.naechste && (e.reif
                          ? <Badge className="bg-emerald-600 text-white text-[10px]"><Check className="w-3 h-3 mr-1" />reif</Badge>
                          : <span className="text-xs text-muted-foreground">{e.fehlt.join(' · ')}</span>)}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {e.letzte_graduierung && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs mr-1" title={`Urkunde ${e.letzte_graduierung.label}`} onClick={() => urkundeOeffnen(e.member_id, e.letzte_graduierung.id)} data-testid={`grad-urkunde-${e.member_id}`}>
                            <FileText className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 text-xs mr-1" title="Gürtel und Einheiten von Hand setzen (ohne Benachrichtigung)"
                          onClick={() => setEinstufung({ e, stufe: e.belt || stufenListe[0]?.key || '', einheiten: String(e.trainings ?? '') })} data-testid={`grad-einstufen-${e.member_id}`}>Einstufen</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs mr-1" disabled={busy === e.member_id || e.streifen >= e.streifen_je_gurt}
                          onClick={() => streifen(e)} data-testid={`grad-streifen-${e.member_id}`}>
                          <Plus className="w-3 h-3 mr-0.5" /> Streifen
                        </Button>
                        {e.naechste && (
                          <Button size="sm" className="h-7 text-xs" variant={e.reif ? 'default' : 'ghost'} disabled={busy === e.member_id}
                            onClick={() => graduieren(e)} data-testid={`grad-graduieren-${e.member_id}`}>
                            <Award className="w-3 h-3 mr-0.5" /> {e.naechste.label}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      {einstufung && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEinstufung(null)}>
          <div className="w-full max-w-sm rounded-xl bg-background border border-border p-4 space-y-3" onClick={(ev) => ev.stopPropagation()} data-testid="grad-einstufen-dialog">
            <p className="font-semibold">Einstufen · {einstufung.e.name}</p>
            <p className="text-xs text-muted-foreground">Setzt den Gürtel ohne Benachrichtigung ans Mitglied und trägt fehlende Einheiten bis zur Zielzahl nach. Vorhandene Einheiten werden nie gelöscht.</p>
            <label className="block space-y-1 text-xs"><span className="text-muted-foreground">Gürtel</span>
              <select value={einstufung.stufe} onChange={(ev) => setEinstufung(x => ({ ...x, stufe: ev.target.value }))} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {stufenListe.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select></label>
            <label className="block space-y-1 text-xs"><span className="text-muted-foreground">Einheiten gesamt (aktuell {einstufung.e.trainings})</span>
              <Input inputMode="numeric" value={einstufung.einheiten} onChange={(ev) => setEinstufung(x => ({ ...x, einheiten: ev.target.value.replace(/\D/g, '') }))} className="h-9" /></label>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEinstufung(null)}>Abbrechen</Button>
              <Button size="sm" disabled={busy === einstufung.e.member_id || !einstufung.stufe} onClick={einstufenSpeichern} data-testid="grad-einstufen-speichern">Speichern</Button>
            </div>
          </div>
        </div>
      )}
      </Card>
    </div>
  );
}
